import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  ENVIRONMENT,
  OriginationBorrower,
  SwitchBorrowerRequest,
  SwitchBorrowerResponse,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class BorrowerSwitchingService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(AuthService);

  private readonly _activeBorrowerId = signal<string | null>(null);

  readonly activeBorrowerId = this._activeBorrowerId.asReadonly();

  private readonly _availableBorrowers = signal<OriginationBorrower[]>([]);

  readonly availableBorrowers = this._availableBorrowers.asReadonly();

  private readonly _currentBorrower = signal<OriginationBorrower | undefined>(undefined);

  readonly currentBorrower = this._currentBorrower.asReadonly();

  private readonly _profileSwitchingAllowed = signal(true);

  readonly profileSwitchingAllowed = this._profileSwitchingAllowed.asReadonly();

  private readonly _pendingSwitchBorrower = signal<OriginationBorrower | null>(null);

  readonly pendingSwitchBorrower = this._pendingSwitchBorrower.asReadonly();

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  initializeActiveBorrower(): void {
    const currentBorrowerId = this.authService.getCpBorrowerId();
    this._activeBorrowerId.set(currentBorrowerId ?? null);
  }

  async switchBorrower(request: SwitchBorrowerRequest): Promise<void> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/auth/switch`;

    const response = await firstValueFrom(this.http.post<SwitchBorrowerResponse>(url, request));

    this.authService.setCpContext({
      authToken: response.authToken,
      loanId: response.loanId,
      borrowerId: response.borrowerId,
      userGuid: response.userGuid,
      expiresInSeconds: response.expiresInSeconds,
      visitorId: response.visitorId,
      mlaDomainLink: response.mlaDomainLink,
      email: response.email,
    });

    this._activeBorrowerId.set(response.borrowerId);
  }

  getCurrentBorrower(borrowers: OriginationBorrower[]): OriginationBorrower | undefined {
    const activeBorrowerId = this._activeBorrowerId();
    if (!activeBorrowerId) {
      return borrowers[0];
    }
    return borrowers.find((b) => b.id === activeBorrowerId) ?? borrowers[0];
  }

  hasMultipleBorrowers(borrowers: OriginationBorrower[]): boolean {
    return borrowers.length > 1;
  }

  setBorrowers(borrowers: OriginationBorrower[]): void {
    const loggedInEmail = this.authService.getUserEmail()?.toLowerCase();
    const hasMatchingEmail = loggedInEmail
      ? borrowers.some((b) => b.email?.toLowerCase() === loggedInEmail)
      : false;
    const filtered = hasMatchingEmail
      ? borrowers.filter((b) => !b.email || b.email.toLowerCase() === loggedInEmail)
      : borrowers;

    let effectiveBorrowers = filtered.length > 0 ? filtered : borrowers;

    const selectedMlaBorrower = this.getSelectedMlaBorrower();
    if (selectedMlaBorrower) {
      const matched = effectiveBorrowers.find(
        (b) =>
          b.firstName?.trim().toLowerCase() === selectedMlaBorrower.firstName?.trim().toLowerCase() &&
          b.lastName?.trim().toLowerCase() === selectedMlaBorrower.lastName?.trim().toLowerCase(),
      );
      if (matched) {
        this._activeBorrowerId.set(matched.id);
        effectiveBorrowers = [matched, ...effectiveBorrowers.filter((b) => b.id !== matched.id)];
      }
    }

    this._availableBorrowers.set(effectiveBorrowers);
    const current = this.getCurrentBorrower(effectiveBorrowers);
    this._currentBorrower.set(current);

    this._profileSwitchingAllowed.set(this.areBorrowerEmailsSame(borrowers));
  }

  requestBorrowerSwitch(borrower: OriginationBorrower): void {
    this._pendingSwitchBorrower.set(borrower);
  }

  clearPendingSwitchRequest(): void {
    this._pendingSwitchBorrower.set(null);
  }

  reset(): void {
    this._activeBorrowerId.set(null);
    this._availableBorrowers.set([]);
    this._currentBorrower.set(undefined);
    this._profileSwitchingAllowed.set(true);
  }

  private getSelectedMlaBorrower(): { firstName: string; lastName: string } | null {
    const json = sessionStorage.getItem('mla_selectedBorrower');
    if (!json) return null;
    try {
      const parsed = JSON.parse(json) as { firstName?: string; lastName?: string; isPrimary?: boolean };
      if (parsed.isPrimary) return null;
      if (!parsed.firstName || !parsed.lastName) return null;
      return { firstName: parsed.firstName, lastName: parsed.lastName };
    } catch {
      return null;
    }
  }

  private areBorrowerEmailsSame(borrowers: OriginationBorrower[]): boolean {
    if (borrowers.length <= 1) return true;
    const emails = borrowers.map((b) => b.email?.toLowerCase().trim()).filter(Boolean);
    if (emails.length <= 1) return true;
    return emails.every((e) => e === emails[0]);
  }
}
